import type { ReactNode } from "react";
import { FeedBotAnimatedMark } from "../../brand/FeedBotAnimatedMark.js";
import { FeedBotGridBackdrop } from "../../brand/FeedBotGridBackdrop.js";
import { motion, useReducedMotion } from "../../../lib/motion.js";
import { cn } from "../../../lib/utils.js";
import { useRouteStatusVisibility } from "./route-status-visibility.js";

const EASE_OUT = [0.22, 1, 0.36, 1] as const;

interface RouteStatusPageProps {
  /** Short status marker, e.g. "404". Rendered as decoration; `title` carries the meaning. */
  code: string;
  title: string;
  description: string;
  action?: ReactNode;
}

/** Holds the first frame until `revealed`, so delays count from when the page is seen. */
function entrance(
  reduceMotion: boolean,
  revealed: boolean,
  delay: number,
  from: { y: number; scale?: number },
  duration: number,
) {
  if (reduceMotion) return { initial: false as const };
  return {
    initial: "hidden",
    animate: revealed ? "shown" : "hidden",
    variants: {
      hidden: { opacity: 0, y: from.y, scale: from.scale ?? 1 },
      shown: { opacity: 1, y: 0, scale: 1, transition: { delay, duration, ease: EASE_OUT } },
    },
  };
}

export function RouteStatusPage({
  code,
  title,
  description,
  action,
}: RouteStatusPageProps): React.JSX.Element {
  const reduceMotion = useReducedMotion() ?? false;
  const revealed = useRouteStatusVisibility();
  const markSize = "size-[84px] sm:size-[104px]";

  return (
    <main className="relative isolate grid min-h-dvh min-w-0 place-items-center overflow-hidden bg-[#f8faf9] px-5 py-10 font-feedbot text-[#17302e] sm:px-6">
      <FeedBotGridBackdrop />
      <div className="grid w-full min-w-0 max-w-[30rem] justify-items-center text-center">
        {revealed ? (
          <FeedBotAnimatedMark className={markSize} />
        ) : (
          <span aria-hidden="true" className={cn("inline-block", markSize)} />
        )}

        <motion.span
          dir="ltr"
          aria-hidden="true"
          className="mt-5 block bg-[linear-gradient(180deg,#1d4d42_0%,#3a8c78_58%,#7cc8b2_100%)] bg-clip-text pe-[0.04em] text-[clamp(84px,12vw,136px)] leading-[0.95] font-[800] tracking-[-0.06em] text-transparent select-none sm:mt-6"
          {...entrance(reduceMotion, revealed, 0.18, { y: 16, scale: 0.96 }, 0.6)}
        >
          {code}
        </motion.span>

        <motion.div
          className="mt-5 grid gap-2.5 sm:mt-6"
          {...entrance(reduceMotion, revealed, 0.3, { y: 8 }, 0.45)}
        >
          <h1 className="m-0 text-[clamp(22px,2.2vw,28px)] leading-tight font-[750] tracking-[-0.035em] text-[#12372e] wrap-anywhere">
            {title}
          </h1>
          <p className="m-0 max-w-[25rem] text-[15px] leading-[1.75] text-[#6f847c] wrap-anywhere">
            {description}
          </p>
        </motion.div>

        {action ? (
          <motion.div className="mt-8" {...entrance(reduceMotion, revealed, 0.38, { y: 8 }, 0.45)}>
            {action}
          </motion.div>
        ) : null}
      </div>
    </main>
  );
}
